if (!customElements.get('fw-delivery-day')) {
  class FWDeliveryDay extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key;
      this.position = this.dataset.suggestionsPosition || 'below';

      this.input = this.querySelector(`#postcode-autocomplete_${this.key}`);
      this.postcodeField = this.querySelector(`#postcode-suburb-value_${this.key}`);
      this.deliveryField = this.querySelector(`#delivery-day-field_${this.key}`);
      this.message = this.querySelector('.fw-delivery-day__message');
      this.suggestionsBox = this.querySelector('.fw-delivery-day__suggestions');

      this.deliveryDayResultStr = this.dataset.deliveryDayResultStr;
      this.deliveryDayNoResultStr = this.dataset.deliveryDayNoResultStr;

      this.suggestionElements = [];
      this.highlightedIndex = -1;

      // Store original values
      this.originalLabel = this.postcodeField.value;
      this.originalDeliveryDay = this.deliveryField.value;

      // Event listeners
      this.handleInput = this.handleInput.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleFocus = () => this.input.select();

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeyDown);
      this.input.addEventListener('focus', this.handleFocus);
      document.addEventListener('click', this.handleOutsideClick);

      // Event listener for restoring the original values
      this.restoreHandler = () => {
        this.input.value = this.originalLabel;
        this.postcodeField.value = this.originalLabel;
        this.deliveryField.value = this.originalDeliveryDay;

        this.message.innerHTML = this.originalDeliveryDay
          ? this.deliveryDayResultStr.replace('{postcode}', this.originalLabel).replace('{delivery_day}', this.originalDeliveryDay)
          : this.deliveryDayNoResultStr.replace('{postcode}', this.originalLabel);

        this.message.classList.toggle('fw-delivery-day__message--success', !!this.originalDeliveryDay);
        this.message.classList.toggle('fw-delivery-day__message--error', !this.originalDeliveryDay);
      };

      this.addEventListener('fw:reset-selection', this.restoreHandler);
    }

    disconnectedCallback() {
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('keydown', this.handleKeyDown);
      this.input.removeEventListener('focus', this.handleFocus);
      document.removeEventListener('click', this.handleOutsideClick);

      // Clean up event listeners on suggestion elements
      this.suggestionElements.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });

      this.removeEventListener('fw:reset-selection', this.restoreHandler);

      this.suggestionElements = [];
    }

    handleInput() {
      if (this.input.value.length < 2) {
        this.suggestionsBox.style.display = 'none';
        return;
      }

      // Clean up any previous suggestion listeners before creating new ones
      this.suggestionElements.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      this.suggestionElements = [];
      this.highlightedIndex = -1;

      const value = this.input.value.toLowerCase();
      const suggestions = window.fwDeliverySuggestions?.[this.key] || [];
      const matches = suggestions
        .map(label => {
          const [postcode, suburb] = label.toLowerCase().split(' - ');
          const score = (value.includes(postcode) ? 1 : 0) + (value.includes(suburb) ? 1 : 0);
          return { label, score };
        })
        .filter(({ label }) => {
          const [postcode, suburb] = label.toLowerCase().split(' - ');
          const terms = value.split(' ').filter(Boolean);
          return terms.every(term =>
            postcode.includes(term) || suburb.includes(term)
          );
        })
        .sort((a, b) => b.score - a.score)
        .map(({ label }) => label)
        .slice(0, 5);

      this.suggestionsBox.innerHTML = '';
      this.suggestionsBox.style.display = 'block';

      // Set position
      const positionStyle = this.position === 'above' ? 'auto' : '100%';
      const oppositeStyle = this.position === 'above' ? '100%' : 'auto';
      this.suggestionsBox.style.top = this.position === 'below' ? positionStyle : 'auto';
      this.suggestionsBox.style.bottom = this.position === 'above' ? oppositeStyle : 'auto';

      matches.forEach(match => {
        const div = document.createElement('div');
        div.textContent = match;
        div.className = 'fw-delivery-day__suggestion';
        const handler = () => this.selectSuggestion(match, this.key);
        div.addEventListener('click', handler);
        this.suggestionsBox.appendChild(div);
        this.suggestionElements.push({ element: div, handler });
      });

      // Add fallback suggestion
      const fallbackLabel = (this.dataset.deliveryNoPostcodeOption || 'My postcode isn\'t listed.');
      const fallbackDiv = document.createElement('div');
      fallbackDiv.textContent = fallbackLabel;
      fallbackDiv.className = 'fw-delivery-day__suggestion fw-delivery-day__suggestion--fallback';
      const fallbackHandler = () => this.selectSuggestion(fallbackLabel, this.key);
      fallbackDiv.addEventListener('click', fallbackHandler);
      this.suggestionsBox.appendChild(fallbackDiv);
      this.suggestionElements.push({ element: fallbackDiv, handler: fallbackHandler });
    }

    handleKeyDown(event) {
      if (!this.suggestionElements.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestionElements.length;
        this.updateHighlight();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.highlightedIndex = (this.highlightedIndex - 1 + this.suggestionElements.length) % this.suggestionElements.length;
        this.updateHighlight();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.highlightedIndex >= 0) {
          const { element } = this.suggestionElements[this.highlightedIndex];
          element.click();
        }
      } else if (event.key === 'Escape') {
        this.suggestionsBox.style.display = 'none';
      }
    }

    updateHighlight() {
      this.suggestionElements.forEach(({ element }, i) => {
        element.classList.toggle('highlighted', i === this.highlightedIndex);
      });
    }

    selectSuggestion(label, key) {
      const deliveryDay = window.fwDeliveryMap?.[key]?.[label] || '';

      if (deliveryDay) {
        this.input.value = label;
        this.postcodeField.value = label;
        this.deliveryField.value = deliveryDay;
      } else {
        this.input.value = '';
        this.postcodeField.value = '';
        this.deliveryField.value = '';
      }

      this.message.innerHTML = deliveryDay
        ? this.deliveryDayResultStr.replace('{postcode}', label).replace('{delivery_day}', deliveryDay)
        : this.deliveryDayNoResultStr.replace('{postcode}', label);

      this.message.classList.toggle('fw-delivery-day__message--success', !!deliveryDay);
      this.message.classList.toggle('fw-delivery-day__message--error', !deliveryDay);
      this.suggestionsBox.style.display = 'none';

      this.dispatchEvent(new CustomEvent('fw:delivery-selection', { detail: { label, deliveryDay } }));
    }

    handleOutsideClick(event) {
      if (!this.contains(event.target)) {
        this.suggestionsBox.style.display = 'none';
      }
    }
  }

  customElements.define('fw-delivery-day', FWDeliveryDay);
}

if (!customElements.get('fw-delivery-day-cart')) {
  class FWDeliveryDayCart extends HTMLElement {
    connectedCallback() {
      this.deliveryDayAttrKey = this.dataset.deliveryDayAttrKey;
      this.postcodeAttrKey = this.dataset.postcodeAttrKey;

      this.displayDayEl = this.querySelector('[data-display-day]');
      this.displayPostcodeEl = this.querySelector('[data-display-postcode]');

      this.optionEl = this.querySelector('fw-delivery-day');
      this.editBtn = this.querySelector('.fw-cart-delivery-day__edit');

      this._handleDeliverySelection = (e) => {
        const { label, deliveryDay } = e.detail;
        this.updateCartAttributes(label, deliveryDay);
      };

      if (this.optionEl) {
        this.optionEl.addEventListener('fw:delivery-selection', this._handleDeliverySelection);
      }
    }

    disconnectedCallback() {
      if (this.optionEl && this._handleDeliverySelection) {
        this.optionEl.removeEventListener('fw:delivery-selection', this._handleDeliverySelection);
      }
      if (this._handleEditClick) {
        this.editBtn?.removeEventListener('click', this._handleEditClick);
      }
    }

    async updateCartAttributes(postcode, deliveryDay) {
      if (!deliveryDay || !postcode) {
        // Add a one-time listener to trigger reset if the user tries to re-open the editor
        this._handleEditClick = () => {
          if (this.optionEl) {
            this.optionEl.dispatchEvent(new CustomEvent('fw:reset-selection', { bubbles: true }));
          }
        };
        this.editBtn?.addEventListener('click', this._handleEditClick, { once: true });
        return;
      }

      this.classList.add('loading');

      try {
        const body = {
          attributes: {
            [this.deliveryDayAttrKey]: deliveryDay,
            [this.postcodeAttrKey]: postcode,
          },
        };

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        // Update the display
        if (this.displayDayEl) this.displayDayEl.textContent = deliveryDay;
        if (this.displayPostcodeEl) this.displayPostcodeEl.textContent = postcode;

        // Close collapsible
        if (this.editBtn) this.editBtn.click();

      } catch (error) {
        console.error('Failed to update cart attributes:', error);
      }

      this.classList.remove('loading');
    }
  }

  customElements.define('fw-delivery-day-cart', FWDeliveryDayCart);
}

if (!customElements.get('fw-delivery-day-ajax-cart')) {
  class FWDeliveryDayAjaxCart extends HTMLElement {
    connectedCallback() {
      this.loadDeliveryDayCartBlocks();
    }

    disconnectedCallback() {
      // Nothing to clean up currently
    }

    async loadDeliveryDayCartBlocks() {
      try {
        let hasFWDeliveryDayCart = true;
        const response = await fetch('/cart');
        const htmlText = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        // Get fw-delivery-day-cart blocks from the fetched cart page
        let newBlocks = doc.querySelectorAll('fw-delivery-day-cart');
        if (!newBlocks.length) {
          hasFWDeliveryDayCart = false;
          newBlocks = doc.querySelectorAll('[data-fw-delivery-day-cart="none"]');
        }

        // Optional: Clear existing content
        this.innerHTML = '';

        newBlocks.forEach(block => {
          // Clone the block
          const cloned = block.cloneNode(true);

          // Move all inline scripts out, then re-add them
          const scripts = cloned.querySelectorAll('script');

          scripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
            } else {
              newScript.textContent = script.textContent;
            }
            // Optional: preserve type, async, etc.
            if (script.type) newScript.type = script.type;
            script.replaceWith(newScript);
          });

          this.appendChild(cloned);
        });

        this.classList.remove('loading');

        if (!hasFWDeliveryDayCart) {
          this.classList.add('hide');
        }

      } catch (error) {
        console.error('Failed to load fw-delivery-day-cart blocks:', error);
      }
    }
  }

  customElements.define('fw-delivery-day-ajax-cart', FWDeliveryDayAjaxCart);
}
